/**
 * Multi-Tenancy Utility Functions
 *
 * This module provides utility functions for managing tenant context in the FlexGym
 * multi-tenant application. Tenants (gyms) are identified through:
 * 1. Subdomain (primary method for whitelabel web apps)
 * 2. X-Tenant-ID header (fallback)
 * 3. tenantId in the login request body (for general apps)
 */

// Storage key for tenant ID
const TENANT_STORAGE_KEY = 'tenant_id';

// Default tenant (can be overridden via environment variable)
const DEFAULT_TENANT = process.env.REACT_APP_DEFAULT_TENANT || '';

// Base domain for subdomain extraction (e.g., 'thenotfit.com' or 'yourplatform.com')
const BASE_DOMAIN = process.env.REACT_APP_BASE_DOMAIN || '';

// Custom domain to tenant mapping.
// Format:
// REACT_APP_TENANT_DOMAIN_MAP=flex:flexgym.al|www.flexgym.al,repeat:repeat.al
const TENANT_DOMAIN_MAP =
  process.env.REACT_APP_TENANT_DOMAIN_MAP ||
  process.env.NEXT_PUBLIC_TENANT_DOMAIN_MAP ||
  '';

// Temporary hardcoded fallback mappings for production domains.
// Keep tenant IDs as real backend tenant IDs (e.g., flexgym), not landing aliases.
const HARDCODED_DOMAIN_TENANT_MAP: Record<string, string> = {
  'flexgym.al': 'flexgym',
  'www.flexgym.al': 'flexgym',
};

function parseTenantDomainMap(): Record<string, string> {
  const mapping: Record<string, string> = {};
  if (!TENANT_DOMAIN_MAP) {
    return mapping;
  }

  const tenantEntries = TENANT_DOMAIN_MAP.split(',');
  for (const entry of tenantEntries) {
    const [tenantRaw, domainsRaw] = entry.split(':');
    const tenant = (tenantRaw || '').trim();
    if (!tenant || !domainsRaw) {
      continue;
    }

    const domains = domainsRaw.split('|').map((domain) => domain.trim().toLowerCase()).filter(Boolean);
    for (const domain of domains) {
      mapping[domain] = tenant;
    }
  }

  return mapping;
}

const CUSTOM_DOMAIN_TENANT_MAP = parseTenantDomainMap();

function getTenantFromCustomDomain(hostname: string): string | null {
  const normalizedHost = hostname.toLowerCase();
  const hardcodedTenant = HARDCODED_DOMAIN_TENANT_MAP[normalizedHost];
  if (hardcodedTenant) {
    console.log('[Tenant] Hardcoded custom domain detected:', normalizedHost, '→', hardcodedTenant);
    return hardcodedTenant;
  }

  const mappedTenant = CUSTOM_DOMAIN_TENANT_MAP[normalizedHost];
  if (mappedTenant) {
    console.log('[Tenant] Custom domain detected:', normalizedHost, '→', mappedTenant);
    return mappedTenant;
  }
  return null;
}

/**
 * Extract tenant ID from the current subdomain
 * Examples:
 * - flexgym.yourplatform.com → flexgym
 * - goldsgym.yourplatform.com → goldsgym
 * - flex.localhost → flex (development)
 * - localhost:3000 → null (no subdomain)
 */
export function getTenantFromSubdomain(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const hostname = window.location.hostname;

  // 0) Custom domain mapping (e.g., flexgym.al -> flex)
  const customDomainTenant = getTenantFromCustomDomain(hostname);
  if (customDomainTenant) {
    return customDomainTenant;
  }

  // 1) Development: Handle *.localhost subdomains (e.g., flex.localhost)
  if (hostname.endsWith('.localhost')) {
    const subdomain = hostname.replace('.localhost', '');
    if (subdomain && subdomain !== 'www') {
      console.log('[Tenant] Development subdomain detected:', subdomain);
      return subdomain;
    }
  }

  // 2) Skip extraction for plain localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }

  // 3) If BASE_DOMAIN is configured, extract subdomain relative to it
  if (BASE_DOMAIN) {
    const baseDomainPattern = new RegExp(
      `^(.+)\\.${BASE_DOMAIN.replace(/\./g, '\\.')}$`
    );
    const match = hostname.match(baseDomainPattern);
    if (match && match[1]) {
      // Exclude 'www' as a tenant
      if (match[1] !== 'www') {
        console.log('[Tenant] BASE_DOMAIN subdomain detected:', match[1]);
        return match[1];
      }
    }
  }

  // 4) Fallback: Extract first subdomain from hostname
  const parts = hostname.split('.');
  if (parts.length > 2) {
    const subdomain = parts[0];
    // Exclude common non-tenant subdomains
    if (subdomain !== 'www' && subdomain !== 'api' && subdomain !== 'app') {
      console.log('[Tenant] Generic subdomain detected:', subdomain);
      return subdomain;
    }
  }

  return null;
}

/**
 * Get the current tenant ID from available sources
 * Priority:
 * 1. Query parameter (development only: ?tenant=xxx)
 * 2. Custom domain or subdomain (for whitelabel web apps)
 * 3. Stored tenant ID (from login response)
 * 4. Default tenant (from environment)
 */
export function getCurrentTenantId(): string | null {
  // Development: Allow ?tenant=xxx query parameter for testing
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    const params = new URLSearchParams(window.location.search);
    const tenantParam = params.get('tenant');
    if (tenantParam) {
      console.log('[Tenant] Query parameter detected:', tenantParam);
      return tenantParam;
    }
  }

  // First, try to get from subdomain
  const subdomainTenant = getTenantFromSubdomain();
  if (subdomainTenant) {
    return subdomainTenant;
  }

  // Then, try to get from localStorage
  if (typeof window !== 'undefined') {
    const storedTenant = localStorage.getItem(TENANT_STORAGE_KEY);
    if (storedTenant) {
      return storedTenant;
    }
  }

  // Finally, return default tenant if configured
  return DEFAULT_TENANT || null;
}

/**
 * Store the tenant ID (from login response)
 */
export function setTenantId(tenantId: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
  }
}

/**
 * Clear the stored tenant ID (on logout)
 */
export function clearTenantId(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(TENANT_STORAGE_KEY);
  }
}

/**
 * Get headers for API requests with tenant context
 */
export function getTenantHeaders(): Record<string, string> {
  const tenantId = getCurrentTenantId();
  if (tenantId) {
    return { 'X-Tenant-ID': tenantId };
  }
  return {};
}

/**
 * Check if the app is running in multi-tenant mode
 * (i.e., has a tenant from subdomain or environment)
 */
export function isMultiTenantEnabled(): boolean {
  return Boolean(getCurrentTenantId());
}

/**
 * Check if tenant switching is allowed
 * (disabled for whitelabel apps with fixed subdomain)
 */
export function canSwitchTenant(): boolean {
  // If tenant is from domain context, switching is not allowed
  const domainTenant = getTenantFromSubdomain();
  return !domainTenant;
}
